import React, { useEffect, useState } from "react";
import _ from "lodash";
import { Eye, EyeOff } from "lucide-react";
import OrbitingIcons from "../OrbitingIcons/OrbitingIcons";
import { verifyEmail, userLogin } from "../../../services/v2/signupService";
import { resetPassword } from "../../../services/v2/loginService";
import { updateUserDetailsToStorage } from "../../../helpers/authHelpers";
import { useAppConfig } from "../../../appConfig";
import { emailValidation } from "../../../helpers/validations";
import "./TenantLoginPage.scss";

const DEFAULT_SPLASH_GRADIENT =
  "linear-gradient(135deg, #060d1a 0%, #0a1628 50%, #0f1f3a 100%)";

const TenantLoginPage = ({ onLoginSuccess }) => {
  const { tenant } = useAppConfig() || {};
  const themeVars = tenant?.themeVars || {};
  const splashGradient =
    themeVars["--splash-gradient"] || DEFAULT_SPLASH_GRADIENT;
  const accentColor = themeVars["--tenant-accent-color"] || "#93c5fd";
  const logoSrc = tenant?.assets?.logos?.logoWhite || "";
  const samlConfig = tenant?.auth?.saml || {};
  const isSamlEnabled = Boolean(samlConfig.enabled && samlConfig.loginUrl);
  const apiBase = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
  const samlHref = isSamlEnabled ? `${apiBase}${samlConfig.loginUrl}` : null;

  const [view, setView] = useState("choose");
  const [formData, setFormData] = useState({ email: "", pwd: "" });
  const [loginError, setLoginError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);

  useEffect(() => {
    if (tenant?.head?.title) document.title = tenant.head.title;
  }, [tenant?.head?.title]);

  const updateField = (name) => (e) => {
    setLoginError(null);
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const continueWithMls = () => {
    if (samlHref) window.location.href = samlHref;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setLoginError(null);

    const form = event.currentTarget;
    const emailEl = form.querySelector('input[type="email"]');
    const pwdEl = form.querySelector('.tenant-login__input--pwd');
    const email = (emailEl?.value || formData.email || "").trim();
    const password = pwdEl?.value || formData.pwd;

    if (!emailValidation(email)) {
      setLoginError("Invalid email address");
      return;
    }
    if (!password) {
      setLoginError("Password is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await verifyEmail({ Email: email }).then((r) => r?.result);
      const exists = _.get(res, "UserExists", false);
      const verified = _.get(res, "IsVerified", false);
      const deleted = _.get(res, "IsDeleted", false);
      if (!exists || !verified || deleted) {
        setLoginError("Account not found or not verified");
        setIsSubmitting(false);
        return;
      }
    } catch {
      setLoginError("Unable to verify account");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await userLogin({ email, password });
      if (_.get(response, "errorCode") === 0) {
        updateUserDetailsToStorage(response);
        localStorage.setItem("_loginTs", String(Date.now()));
        if (typeof onLoginSuccess === "function") onLoginSuccess();
        else window.location.reload();
      } else {
        setLoginError(_.get(response, "message", "Invalid credentials"));
      }
    } catch (err) {
      setLoginError(err?.response?.data?.message || "Login failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgot = async () => {
    setResetMsg(null);
    const email = formData.email.trim();
    if (!emailValidation(email)) {
      setLoginError("Enter your email above, then tap reset.");
      return;
    }
    setLoginError(null);
    try {
      await resetPassword({ email });
    } catch {
      setResetMsg(null);
    }
    setResetMsg("If an account exists for that email, we've sent a reset link.");
  };

  return (
    <section
      className="tenant-login"
      style={{ "--tenant-accent-color": accentColor }}
    >
      <div
        className="tenant-login__visual"
        style={{ background: splashGradient }}
      >
        {logoSrc && (
          <img className="tenant-login__logo" src={logoSrc} alt="Logo" />
        )}
        <OrbitingIcons />
        <span
          className="tenant-login__anchor"
          style={{
            background: accentColor,
            boxShadow: `0 0 24px 8px ${accentColor}33`,
          }}
        />
      </div>

      <div className="tenant-login__panel">
        {logoSrc && (
          <img
            className="tenant-login__logo tenant-login__logo--mobile"
            src={logoSrc}
            alt="Logo"
          />
        )}

        <div className="tenant-login__card">
          <h1 className="tenant-login__title">Welcome back</h1>
          <p className="tenant-login__subtitle">
            Sign in to access real-time buyer demand data for your market.
          </p>

          {view === "choose" ? (
            <div className="tenant-login__choices">
              <button
                type="button"
                className="btn btn-primary tenant-login__sso"
                onClick={continueWithMls}
              >
                <span className="tenant-login__sso-label">Continue with MLS</span>
              </button>

              <button
                type="button"
                className="btn outline-btn tenant-login__email-cta"
                onClick={() => setView("email")}
              >
                Continue with Email
              </button>
            </div>
          ) : (
            <form
              className="tenant-login__form"
              onSubmit={handleSubmit}
              noValidate
            >
              <button
                type="button"
                className="tenant-login__back"
                onClick={() => {
                  setView("choose");
                  setLoginError(null);
                  setResetMsg(null);
                }}
              >
                &#8592; Back
              </button>

              <label className="tenant-login__field">
                <span className="tenant-login__label">Email</span>
                <input
                  className="tenant-login__input"
                  type="email"
                  placeholder="yourname@email.com"
                  autoFocus
                  value={formData.email}
                  onChange={updateField("email")}
                  required
                />
              </label>

              <label className="tenant-login__field">
                <span className="tenant-login__label">Password</span>
                <div className="tenant-login__pwd-wrap">
                  <input
                    className="tenant-login__input tenant-login__input--pwd"
                    type={showPwd ? "text" : "password"}
                    placeholder="Enter your password"
                    value={formData.pwd}
                    onChange={updateField("pwd")}
                    required
                  />
                  <button
                    type="button"
                    className="tenant-login__pwd-toggle"
                    onClick={() => setShowPwd((s) => !s)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                  >
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>

              {loginError && (
                <div className="tenant-login__error" role="alert">
                  {loginError}
                </div>
              )}

              <button
                className="btn btn-primary tenant-login__submit"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Signing in..." : "Sign In"}
              </button>

              {resetMsg && (
                <div className="tenant-login__reset-msg" role="status">
                  {resetMsg}
                </div>
              )}

              <button
                type="button"
                className="tenant-login__forgot"
                onClick={handleForgot}
              >
                Forgot your password?
              </button>
            </form>
          )}

          <p className="tenant-login__legal">
            By continuing, you agree to our{" "}
            <a
              href="https://gitcha.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use
            </a>
            . Read our{" "}
            <a
              href="https://gitcha.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export default TenantLoginPage;
